import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Alert } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { validateChartFile, ACCEPTED_CHART_TYPES } from './ChartUpload.validate';

interface Props {
  file: File | null;
  onFile: (file: File | null) => void;
  error?: string;
}

export const ChartUpload: React.FC<Props> = ({ file, onFile, error }) => {
  const [localError, setLocalError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    if (!picked) {
      onFile(null);
      setLocalError(null);
      return;
    }
    const validationError = await validateChartFile(picked);
    if (validationError) {
      setLocalError(validationError);
      onFile(null);
      return;
    }
    setLocalError(null);
    onFile(picked);
  };

  return (
    <>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Chart image
      </Typography>
      <Button component="label" variant="outlined" startIcon={<UploadFileIcon />}>
        {file ? 'Replace image' : 'Upload chart (PNG / JPG / SVG, ≤5MB, ≥400×250)'}
        <input
          type="file"
          hidden
          accept={ACCEPTED_CHART_TYPES.join(',')}
          onChange={handleChange}
        />
      </Button>
      {(localError || error) && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {localError || error}
        </Alert>
      )}
      {previewUrl && (
        <Box sx={{ mt: 2 }}>
          <Box
            component="img"
            src={previewUrl}
            alt="Chart preview"
            sx={{ maxWidth: '100%', maxHeight: 280, border: '1px solid #DCD6D2', borderRadius: 1 }}
          />
        </Box>
      )}
    </>
  );
};
